import type { ContactFormValues, Contact } from "./core/contact";
import type { Address } from "./core/address";
import type {
  Company,
  User,
  CheckoutFormValues,
  WasteAuditFormValues,
  ContactPageFormValues,
  AdminUserFormValues,
  AdminContactFormValues,
  AdminCompanyFormValues,
  FreightCalculatorValues,
  SimpleCalculatorValues,
  CrmContactDialogValues,
} from "./schemas";
import { DEFAULT_GLOBAL_ROLE } from "@shared/auth/roles";
import { ACCOUNT_TYPE } from "@convex/constants/types.lib";

export const defaultContact: ContactFormValues = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  company: "",
};

export const defaultAddress: Address = {
  line1: "",
  line2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "US",
};

export const defaults = {
  contact: {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    title: "",
    company: "",
  } satisfies Contact,

  address: {
    line1: "",
    line2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "US",
  } satisfies Address,

  company: {
    name: "",
    email: "",
    phone: "",
    website: "",
    accountType: undefined,
  } satisfies Company,

  user: {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    title: "",
  } satisfies User,

  checkout: {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    line1: "",
    city: "",
    state: "",
    postalCode: "",
    deliveryDate: "",
  } satisfies CheckoutFormValues,

  wasteAuditForm: {
    company: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    packagingMaterials: [],
    estimatedVolume: "",
    service: "Buy",
    additionalInfo: "",
    botcheck: false,
  } satisfies WasteAuditFormValues,

  contactPageForm: {
    name: "",
    email: "",
    phone: "",
    company: "",
    orderReference: "",
    inquiryType: "support",
    message: "",
    consent: false,
    smsConsent: false,
  } satisfies ContactPageFormValues,

  adminUserForm: {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    title: "",
    role: DEFAULT_GLOBAL_ROLE,
    companyId: "",
  } satisfies AdminUserFormValues,

  adminContactForm: {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    title: "",
    company: "",
    contactType: "buyer",
    billingAddress: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "US",
    },
    shippingAddress: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "US",
    },
    sameAsShipping: true,
    sendInvitation: true,
    createStripeCustomer: true,
  } satisfies AdminContactFormValues,

  crmContactDialog: {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    title: "",
    company: "",
    contactType: "buyer",
  } satisfies CrmContactDialogValues,

  adminCompanyForm: {
    name: "",
    website: "",
    companyEmail: "",
    companyPhone: "",
    accountType: ACCOUNT_TYPE.VENDOR,
    legalBusinessName: "",
    ein: "",
    businessStructure: "",
    w9Document: undefined,
    sendOnboarding: false,
  } satisfies AdminCompanyFormValues,

  freightCalculator: {
    origin: "",
    destination: "",
    quantity: 22,
    weight_lbs: 20000,
    equipment_type: "DRY",
  } satisfies FreightCalculatorValues,

  simpleCalculator: {
    origin: "",
    destination: "",
    pickup: (() => {
      const today = new Date();
      today.setDate(today.getDate() + 3);
      return today.toISOString().slice(0, 10);
    })(),
    type: "DRYVAN",
  } satisfies SimpleCalculatorValues,
};

export const contactDefaultValues = defaults.contact;
export const addressDefaultValues = defaults.address;
export const companyDefaultValues = defaults.company;
export const userDefaultValues = defaults.user;
export const checkoutDefaultValues = defaults.checkout;

export const wasteAuditFormDefaultValues = defaults.wasteAuditForm;
export const contactPageFormDefaultValues = defaults.contactPageForm;
export const adminUserFormDefaultValues = defaults.adminUserForm;
export const adminContactFormDefaultValues = defaults.adminContactForm;
export const adminCompanyFormDefaultValues = defaults.adminCompanyForm;
export const crmContactDialogDefaultValues = defaults.crmContactDialog;
export const freightCalculatorDefaultValues = defaults.freightCalculator;
export const simpleCalculatorDefaultValues = defaults.simpleCalculator;
